import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { derivePermissions } from '@/lib/patient-permissions';
import type { Database } from '@/integrations/supabase/types';

type AppRole = Database['public']['Enums']['app_role'];
type PatientStatus = 'active' | 'suspended' | 'archived';

interface PatientRoleResult {
  role: AppRole | null;
  isLoading: boolean;
  isOwner: boolean;
  isObserver: boolean;
  isCaregiver: boolean;
  canEdit: boolean;
  canInvite: boolean;
  canManageTeam: boolean;
  canExecuteTasks: boolean;
  canViewDashboard: boolean;
  canEditInventory: boolean;
  canEditCarePlan: boolean;
  canManageSchedules: boolean;
  canAssignShiftTemplates: boolean;
  isCareConfigDelegate: boolean;
  status: PatientStatus | null;
  isPatientActive: boolean;
  isPatientSuspended: boolean;
  isPatientArchived: boolean;
  canWrite: boolean;
}

export function usePatientRole(patientId: string | undefined): PatientRoleResult {
  const { user } = useAuth();

  const { data: link, isLoading: isRoleLoading } = useQuery({
    queryKey: ['patient-role', patientId, user?.id],
    queryFn: async () => {
      if (!user || !patientId) return null;

      const { data, error } = await supabase
        .from('patient_user_links')
        .select('role, can_manage_care_config')
        .eq('patient_id', patientId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching patient role:', error);
        return null;
      }

      return data ?? null;
    },
    enabled: !!user && !!patientId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const role = link?.role ?? null;


  // Check company role for this patient
  const { data: companyRole, isLoading: isCompanyLoading } = useQuery({
    queryKey: ['patient-company-role', patientId, user?.id],
    queryFn: async () => {
      if (!user || !patientId) return null;

      // First get company IDs linked to this patient
      const { data: assignments, error: aErr } = await supabase
        .from('company_patient_assignments')
        .select('company_id')
        .eq('patient_id', patientId)
        .eq('is_active', true);

      if (aErr || !assignments?.length) return null;

      const companyIds = assignments.map(a => a.company_id);

      // Then check if user is a member of any of those companies
      const { data: membership, error: mErr } = await supabase
        .from('company_members')
        .select('role')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .in('company_id', companyIds)
        .maybeSingle();

      if (mErr || !membership) return null;
      return membership.role as string;
    },
    enabled: !!user && !!patientId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Fetch patient status to gate writes when suspended/archived
  const { data: status, isLoading: isStatusLoading } = useQuery({
    queryKey: ['patient-status', patientId],
    queryFn: async () => {
      if (!patientId) return null;
      const { data, error } = await supabase
        .from('patients')
        .select('status')
        .eq('id', patientId)
        .maybeSingle();
      if (error) {
        console.error('Error fetching patient status:', error);
        return null;
      }
      return (data?.status as PatientStatus) ?? null;
    },
    enabled: !!patientId,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const permissions = derivePermissions(
    role ?? null,
    companyRole ?? null,
    Boolean((link as any)?.can_manage_care_config)
  );
  const isPatientActive = status === 'active' || status == null;
  const isPatientSuspended = status === 'suspended';
  const isPatientArchived = status === 'archived';
  const canWrite = isPatientActive && (permissions.canEdit || permissions.canExecuteTasks || permissions.canEditInventory);

  return {
    role: role ?? null,
    isLoading: isRoleLoading || isCompanyLoading || isStatusLoading,
    ...permissions,
    status: status ?? null,
    isPatientActive,
    isPatientSuspended,
    isPatientArchived,
    canWrite,
  };
}
